import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MultiSelect,
} from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Matter, MedicalTreatment } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import { useRef, useState } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";
interface SelectTreatmentRecordsFormFieldProps {
  className?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  label?: string;
  matter: Matter;
  treatment: MedicalTreatment;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLDivElement>;
  register: UseFormRegisterReturn;
  registerNotApplicable?: UseFormRegisterReturn;
  disabled?: boolean;
}

export const SelectTreatmentRecordFormField = ({
  className,
  label,
  matter,
  treatment,
  onChange,
  placeholder,
  register,
  registerNotApplicable,
  disabled,
  isItemsLoading,
}: SelectTreatmentRecordsFormFieldProps) => {
  const form = useFormContext();
  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  const items = matter.medicalTreatmentRecords.filter(
    (record) => record.medicalTreatmentId === treatment.id,
  );

  const { openTreatmentRecordModal } = useGlobalModal();

  const NOT_APPLICABLE_VALUE = "__NOT_APPLICABLE__";

  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    const values =
      (form.getValues(register.name) as string[] | undefined) ?? [];
    const isNotApplicable = registerNotApplicable
      ? form.getValues(registerNotApplicable.name)
      : false;
    return isNotApplicable ? [NOT_APPLICABLE_VALUE] : values;
  });

  const addNewRecord = () => {
    openTreatmentRecordModal({
      matterId: matter.id,
      treatmentId: treatment.id,
      editing: false,
      initialTab: "details",
      afterCreateFn: (newRecord) => {
        setSelectedValues([...selectedValues, newRecord.data.id]);
        form.setValue(register.name, [...selectedValues, newRecord.data.id], {
          shouldDirty: true,
        });
      },
      creatingFromAppointment: true,
    });
  };

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        return (
          <FormItem>
            {label && (
              <div className="flex">
                <FormLabel>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <FormControl>
              <MultiSelect
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                addNewOption={{
                  label: "Add new record",
                  onClick: addNewRecord,
                }}
                isLoading={isItemsLoading}
                {...field}
                disabled={isInDisabledFieldset || disabled}
                onValueChange={(value) => {
                  const hasNotApplicable = value.includes(NOT_APPLICABLE_VALUE);

                  if (hasNotApplicable) {
                    // If "Not Applicable" is selected, only keep that value
                    const newValue = [NOT_APPLICABLE_VALUE];
                    setSelectedValues(newValue);
                    field.onChange([], { shouldDirty: true });
                    if (registerNotApplicable) {
                      form.setValue(registerNotApplicable.name, true, {
                        shouldDirty: true,
                      });
                    }
                  } else {
                    // If "Not Applicable" is not selected, handle normally
                    setSelectedValues(value);
                    field.onChange(value, { shouldDirty: true });
                    if (registerNotApplicable) {
                      form.setValue(registerNotApplicable.name, false, {
                        shouldDirty: true,
                      });
                    }
                  }
                  onChange?.(value);
                }}
                defaultValue={field.value}
                options={[
                  ...(registerNotApplicable
                    ? [
                        {
                          label: "Not applicable",
                          value: NOT_APPLICABLE_VALUE,
                          context: "Mark as not applicable",
                        },
                      ]
                    : []),
                  ...items.map((item) => ({
                    label: item.name || "<No Label>",
                    value: item.id,
                    context:
                      item.status === "Received"
                        ? `Received on ${
                            item.receivedDate
                              ? formatDate(item.receivedDate)
                              : "<No Date>"
                          }`
                        : `Requested on ${
                            item.requestDate
                              ? formatDate(item.requestDate)
                              : "<No Date>"
                          }`,
                  })),
                ]}
                modalPopover
                className={className}
                maxCount={1}
                placeholder={placeholder}
                ignoreAllSelected={true}
                showToggleAll={false}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};
